import { getReturnStack, getFiberDisplayName, getReactFiberForElement } from './reactFiber.tsx';

const OWNER_PREFIX = 'OwnerMark___';
export type FiberOwnershipTrace = {
	ownershipMarker: string | null;
	domAnchor: string | null;
	domPath: string | null;
	renderParent: string | null;
	cause: string;
	summary: string[];
};

/**
 * extension point -> add ComponentNames to be ignored, like StyledComponents
 */
const EXCLUDE_LIST: ReadonlySet<string> = new Set([
	// emotion
	'StyleCacheProvider',
	'EmotionCssPropInternal',
	// UFO
	'UFOSegment',
	'JiraUFOSegmentWrapper',
]);

const parseOwnershipMarker = (displayName: string): string | null => {
	if (!displayName.startsWith(OWNER_PREFIX)) {
		return null;
	}
	const parts = displayName.slice(OWNER_PREFIX.length).split('___');
	if (parts.length !== 4) {
		return null;
	}
	return `${parts[0]}:${parts[1]}`;
};

const getSafeDomAnchor = (element: HTMLElement): string | null => {
	if (element.dataset.testid) {
		return `testid:${element.dataset.testid}`;
	}
	return null;
};

export const unwrapFiberToOwnershipTrace = (
	element: HTMLElement,
	mismatchServerNode: Node | null,
	mismatchClientNode: Node | null,
): FiberOwnershipTrace | null => {
	const fiber = getReactFiberForElement(element);
	if (!fiber) {
		return null;
	}

	let ownershipMarker: string | null = null;
	let renderParent: string | null = null;

	for (const stackFiber of getReturnStack(fiber)) {
		const displayName = getFiberDisplayName(stackFiber);
		if (!displayName) {
			// oxlint-disable-next-line no-continue
			continue;
		}
		if (ownershipMarker === null) {
			ownershipMarker = parseOwnershipMarker(displayName);
		}
		if (
			renderParent === null &&
			// reduces pickup to a "meaningful name"
			displayName.length > 5 &&
			// removes "known" noise
			!EXCLUDE_LIST.has(displayName) &&
			!displayName.startsWith(OWNER_PREFIX)
		) {
			renderParent = `react:${displayName}`;
		}
		if (ownershipMarker !== null && renderParent !== null) {
			break;
		}
	}

	let domAnchor: string | null = null;
	const pathSegments: string[] = [];
	for (let el: HTMLElement | null = element; el; el = el.parentElement) {
		domAnchor = getSafeDomAnchor(el);
		if (domAnchor) {
			break;
		}
		// Compute sibling index among element siblings
		const siblingIndex = el.parentElement ? Array.from(el.parentElement.children).indexOf(el) : 0;
		pathSegments.push(`${el.tagName.toLowerCase()}:${siblingIndex}`);
	}

	const cause = `${mismatchServerNode?.nodeName.toLowerCase() ?? 'null'}|${mismatchClientNode?.nodeName.toLowerCase() ?? 'null'}`;

	return {
		ownershipMarker,
		domAnchor,
		domPath: pathSegments.length > 0 ? pathSegments.reverse().join('->') : null,
		renderParent,
		cause,
		// oxlint-disable-next-line typescript/consistent-type-assertions
		summary: [ownershipMarker, domAnchor, renderParent, cause].filter(Boolean) as string[],
	};
};
